/**
 * Bandeau de mode observateur — affiché en haut de chaque layout quand
 * un super-admin navigue en tant qu'un autre utilisateur.
 *
 * Le token est stocké dans sessionStorage (par-onglet). "Quitter" efface
 * le token et redirige vers /admin/utilisateurs dans ce même onglet.
 * Fermer l'onglet suffit aussi pour mettre fin à la session d'observation.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useCurrentUser } from '@/lib/auth';
import { IMPERSONATE_STORAGE_KEY } from '@/lib/api';
import { Eye, X } from 'lucide-react';

export function ImpersonationBanner() {
  const { data: user } = useCurrentUser();
  const qc = useQueryClient();
  const navigate = useNavigate();

  if (!user?.impersonatedBy) return null;

  const ROLE_LABEL: Record<string, string> = {
    student: 'Candidat',
    company: 'Entreprise',
    responsable: 'Responsable',
  };

  const handleExit = () => {
    sessionStorage.removeItem(IMPERSONATE_STORAGE_KEY);
    // Vider le cache sans refetch : évite les 403 sur les routes étudiant/entreprise
    // qui seraient déclenchés par invalidateQueries() alors que le token admin est restauré.
    qc.clear();
    navigate('/admin/utilisateurs');
  };

  return (
    <div
      role="region"
      aria-label="Mode observateur actif"
      className="flex items-center justify-between gap-3 border-b-2 border-passage-ink bg-passage-bordeaux px-4 py-2 text-sm font-extrabold text-white"
    >
      <span className="flex items-center gap-2">
        <Eye className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>
          <strong>Mode observateur</strong> — vous naviguez en tant que{' '}
          <span className="underline underline-offset-2">{user.email}</span>
          {user.role && ROLE_LABEL[user.role] ? (
            <span className="ml-1 rounded-full border border-white/40 bg-white/15 px-2 py-0.5 text-[11px] font-extrabold uppercase tracking-widest">
              {ROLE_LABEL[user.role]}
            </span>
          ) : null}
        </span>
      </span>
      <button
        type="button"
        onClick={handleExit}
        className="flex items-center gap-1 rounded-lg border-2 border-white/40 bg-white/10 px-3 py-1 text-xs font-extrabold hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
        aria-label="Quitter le mode observateur"
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
        Quitter
      </button>
    </div>
  );
}
